import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import API from '../shared/forms/API';
import LoadingView from '../shared/LoadingView';
import { FIELD_DEFINITIONS } from './fieldDefinitions';

const PREVIEW_DEBOUNCE_MS = 400;

export default class NotificationConfigEditPageView extends Component {
  state = {
    item: null,
    settings: null,
    values: {},
    fieldErrors: {},
    saving: false,
    deleting: false,
    message: null,
    preview: null,
    previewLoading: false,
    previewError: null,
  };

  previewTimer = null;
  previewToken = 0;

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  target() {
    const t = this.props.match.params.location;
    return t ? decodeURIComponent(t) : null;
  }

  async load() {
    const slug = this.props.match.params.slug;
    const target = this.target();
    try {
      const item = await new API()
        .fetch(`settings/notification-catalog/${slug}`)
        .then((r) => r.json());
      const settings = {};
      const values = {};
      await Promise.all(
        item.fields.map(async (field) => {
          const key = target ? `${item.baseKey}.${target}` : item.baseKey;
          const r = await new API().fetch(`settings/${field}/${key}`);
          if (r.ok) {
            const setting = await r.json();
            settings[field] = setting;
            values[field] = setting.value;
          } else {
            values[field] = '';
          }
        })
      );
      this.setState({ item, settings, values }, () => this.refreshPreview());
    } catch (error) {
      console.error(error);
    }
  }

  isDirty() {
    const { item, settings, values } = this.state;
    if (!item) return false;
    return item.fields.some((field) => {
      const original = (settings[field] && settings[field].value) || '';
      return (values[field] || '') !== original;
    });
  }

  scheduleRefreshPreview = () => {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(this.refreshPreview, PREVIEW_DEBOUNCE_MS);
  };

  refreshPreview = async () => {
    const template = this.state.values.template || '';
    if (!template) {
      this.setState({ preview: null, previewError: null, previewLoading: false });
      return;
    }
    const token = ++this.previewToken;
    this.setState({ previewLoading: true, previewError: null });
    try {
      const response = await new API().submit('settings/render-example', 'POST', {
        render_setting: { type: 'notification', template },
      });
      const json = await response.json();
      if (token !== this.previewToken) return;

      if (!response.ok || (json && json.errors)) {
        this.setState({
          preview: null,
          previewError: 'Sjabloon kon niet worden gerenderd',
          previewLoading: false,
        });
        return;
      }
      const text = typeof json === 'string' ? json : JSON.stringify(json, null, 2);
      this.setState({ preview: text, previewError: null, previewLoading: false });
    } catch (error) {
      if (token !== this.previewToken) return;
      console.error(error);
      this.setState({
        previewError: 'Voorbeeld kon niet worden geladen',
        previewLoading: false,
      });
    }
  };

  handleChange = (field) => (event) => {
    const value = event.target.value;
    this.setState(
      (state) => ({
        values: { ...state.values, [field]: value },
        fieldErrors: { ...state.fieldErrors, [field]: undefined },
      }),
      () => {
        if (field === 'template') this.scheduleRefreshPreview();
      }
    );
  };

  handleDeleteOverride = async () => {
    const { item, settings } = this.state;
    const target = this.target();
    if (!target) return;
    if (!window.confirm(`Override voor "${target}" verwijderen?`)) return;

    this.setState({ deleting: true, message: null });
    const requests = Object.keys(settings).map((field) => {
      const key = `${item.baseKey}.${target}`;
      return new API().fetch(`settings/${field}/${key}`, 'DELETE');
    });
    try {
      await Promise.all(requests);
      this.props.history.push(`/notification-config/${this.props.match.params.slug}/`);
    } catch (error) {
      console.error(error);
      this.setState({
        deleting: false,
        message: { type: 'danger', text: 'Override kon niet worden verwijderd.' },
      });
    }
  };

  handleSave = async (event) => {
    event.preventDefault();
    const { item, settings, values } = this.state;
    const target = this.target();
    this.setState({ saving: true, message: null, fieldErrors: {} });

    const requests = [];
    for (const field of item.fields) {
      const original = (settings[field] && settings[field].value) || '';
      const current = values[field] || '';
      if (current === original) continue;
      const key = target ? `${item.baseKey}.${target}` : item.baseKey;
      const path = `settings/${field}/${key}`;
      if (current === '' && settings[field]) {
        requests.push(
          new API().fetch(path, 'DELETE').then((r) => ({ field, response: r }))
        );
      } else if (current !== '') {
        requests.push(
          new API()
            .submit(path, 'PUT', { setting: { value: current } })
            .then((r) => ({ field, response: r }))
        );
      }
    }

    try {
      const results = await Promise.all(requests);
      const failures = results.filter((r) => !r.response.ok);
      if (failures.length > 0) {
        const fieldErrors = {};
        await Promise.all(
          failures.map(async ({ field, response }) => {
            try {
              const data = await response.json();
              const childErrors =
                data && data.errors && data.errors.children &&
                data.errors.children.value && data.errors.children.value.errors;
              const globalErrors = data && data.errors && data.errors.errors;
              const message =
                (childErrors && childErrors[0]) ||
                (globalErrors && globalErrors[0]) ||
                'Veld kon niet worden opgeslagen.';
              fieldErrors[field] = message;
            } catch (_) {
              fieldErrors[field] = 'Veld kon niet worden opgeslagen.';
            }
          })
        );
        this.setState({
          saving: false,
          fieldErrors,
          message: {
            type: 'danger',
            text: `Niet alle velden zijn opgeslagen (${failures
              .map((f) => f.field)
              .join(', ')}).`,
          },
        });
      } else {
        this.props.history.push(`/notification-config/${this.props.match.params.slug}/`);
      }
    } catch (error) {
      console.error(error);
      this.setState({
        saving: false,
        message: { type: 'danger', text: 'Onverwachte fout' },
      });
    }
  };

  renderField(field) {
    const def = FIELD_DEFINITIONS[field] || { title: field, component: 'text' };
    const inputId = `field-${field}`;
    const error = this.state.fieldErrors[field];
    const inputClass = `form-control${error ? ' is-invalid' : ''}`;
    const value = this.state.values[field] || '';
    return (
      <div className="form-group" key={field}>
        <label htmlFor={inputId}>{def.title}</label>
        {def.component === 'textarea' ? (
          <textarea
            id={inputId}
            rows={def.rows || 14}
            className={inputClass}
            value={value}
            onChange={this.handleChange(field)}
            disabled={this.state.saving}
          />
        ) : (
          <input
            id={inputId}
            type={def.type || 'text'}
            className={inputClass}
            value={value}
            onChange={this.handleChange(field)}
            disabled={this.state.saving}
            maxLength={def.maxLength}
            pattern={def.pattern}
            placeholder={def.placeholder}
          />
        )}
        {error && <div className="invalid-feedback">{error}</div>}
        {def.help && (
          <small className="form-text text-muted">
            {def.help}
            {def.maxLength &&
              ` (${value.length}/${def.maxLength})`}
          </small>
        )}
      </div>
    );
  }

  renderForm() {
    const { item, message, saving } = this.state;
    const dirty = this.isDirty();
    const slug = this.props.match.params.slug;
    return (
      <form onSubmit={this.handleSave}>
        {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}
        {item.fields.map((field) => this.renderField(field))}
        <button type="submit" className="btn btn-primary" disabled={!dirty || saving}>
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
        <Link
          to={`/notification-config/${slug}/`}
          className="btn btn-secondary float-right"
        >
          Annuleren
        </Link>
      </form>
    );
  }

  renderPreview() {
    const { preview, previewError, values } = this.state;
    return (
      <>
        {previewError && <div className="alert alert-warning mb-0">{previewError}</div>}
        {!values.template && !previewError && (
          <p className="text-muted mb-0">
            Voer een berichttekst in om een voorbeeld te zien.
          </p>
        )}
        {preview !== null && !previewError && (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {preview}
          </div>
        )}
      </>
    );
  }

  renderContent() {
    const target = this.target();
    const scope = target ? ` – ${target}` : ' – Standaard';

    return (
      <>
        <h4 className="mb-3">{this.state.item.name}{scope}</h4>
        <div className="row">
          <div className="col-lg-7">
            <div className="card mb-4">
              <div className="card-body">
                {this.renderForm()}
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>Voorbeeld</strong>
                {this.state.previewLoading && (
                  <i className="fa fa-circle-o-notch fa-spin text-muted" />
                )}
              </div>
              <div className="card-body">
                {this.renderPreview()}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  render() {
    const ready = this.state.item !== null;
    const target = this.target();
    const hasOverrideSettings =
      ready && target && Object.keys(this.state.settings || {}).length > 0;
    return (
      <div>
        <nav className="navbar navbar-expand navbar-light bg-light">
          <div className="navbar-brand">Notificatie templates</div>
          {hasOverrideSettings && (
            <button
              type="button"
              className="btn btn-danger ml-auto"
              onClick={this.handleDeleteOverride}
              disabled={this.state.deleting || this.state.saving}
            >
              <i className="fa fa-trash mr-1" />
              {this.state.deleting ? 'Verwijderen…' : 'Verwijderen'}
            </button>
          )}
        </nav>
        <div className="container-fluid my-4">
          {ready ? this.renderContent() : <LoadingView />}
        </div>
      </div>
    );
  }
}
